import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Clock,
  MapPin,
  Building2,
  MessageSquare,
  CheckCircle,
  Camera,
} from 'lucide-react';
import type { Task } from '@/lib/types';

interface TaskDetailsProps {
  task: Task;
}

export function TaskDetails({ task }: TaskDetailsProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{task.title}</CardTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge
              variant={
                task.status === 'completed' ? 'default' :
                task.status === 'in_progress' ? 'secondary' :
                task.status === 'rejected' ? 'destructive' :
                'outline'
              }
            >
              {task.status.replace('_', ' ')}
            </Badge>
            <Badge
              variant={
                task.priority === 'high' ? 'destructive' :
                task.priority === 'medium' ? 'secondary' :
                'outline'
              }
            >
              {task.priority} priority
            </Badge>
            <Badge variant="outline">
              {task.category}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          {task.status === 'completed' && (
            <Button variant="outline">
              <Camera className="mr-2 h-4 w-4" />
              View Evidence
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
            <p className="mt-1">{task.description}</p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Due Date</h3>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>{new Date(task.dueDate).toLocaleDateString()}</span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Property</h3>
              <div className="flex items-center gap-2 mt-1">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>Property ID: {task.propertyId}</span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">Location</h3>
              <div className="flex items-center gap-2 mt-1">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>Building A, Floor 2</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t pt-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-medium">Activity Log</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                <CheckCircle className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm">Task created</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(task.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}